import { RobotOutlined, ToolOutlined } from "@ant-design/icons";
import type {
  AgentActivityEntry,
  AgentActivityProtocol,
} from "@p0security/integrations-agentic-shared/interface";
import type { ReactNode } from "react";

const COMMAND_ARGUMENT_KEYS = ["cli_command", "command"] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** An MCP tool call's arguments usually carry the command it ran, either at
 * the top level or nested under `call`. */
const commandSnippetFromArguments = (
  args: Record<string, unknown> | undefined
) => {
  if (!args) return undefined;
  const nestedCall = isRecord(args.call) ? args.call : undefined;
  for (const candidate of [args, nestedCall]) {
    if (!candidate) continue;
    for (const key of COMMAND_ARGUMENT_KEYS) {
      const value = candidate[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }
  return undefined;
};

type EventEntryOf<Protocol extends AgentActivityProtocol> = Extract<
  AgentActivityEntry,
  { protocol: Protocol }
>;

/**
 * Everything the event log renders differently per protocol.
 *
 * Mirrors the event dispatch the reader uses on the server: one entry per
 * protocol, so a new protocol is one new entry here rather than an edit to
 * every component that shows a call.
 */
export type EventProtocolView<
  Protocol extends AgentActivityProtocol = AgentActivityProtocol,
> = {
  /** Short name, shown on the protocol tag and in the protocol filter. */
  label: string;
  /** What this protocol calls the server a call was made against. */
  serverLabel: string;
  /** Tag colour, or absent for the neutral default. */
  tagColor?: string;
  /** Shown when a call of this protocol names nothing. */
  unnamedLabel: string;
  /** The line under the call name. */
  callDetail(entry: EventEntryOf<Protocol>): string | undefined;
  /** Icon standing for a call of this protocol. */
  icon(className?: string): ReactNode;
  /** What the call names: an agent, or a tool. Declared as a method, so a
   * view for one protocol stays assignable to the registry's general type. */
  callName(entry: EventEntryOf<Protocol>): string | undefined;
  /** The server the call was made against, as {@link serverLabel} names it. */
  serverName(entry: EventEntryOf<Protocol>): string | undefined;
};

const a2aEventView = {
  label: "A2A",
  serverLabel: "Bridge",
  tagColor: "blue",
  unnamedLabel: "Unknown agent",
  // An A2A call carries no command to summarize, so its method is the most
  // useful second line.
  callDetail(entry) {
    return entry.method;
  },
  callName(entry) {
    return entry.agentId;
  },
  icon(className) {
    return <RobotOutlined className={className} aria-hidden />;
  },
  // Bridges can share an upstream, so the id tells them apart.
  serverName({ bridgeBaseUrl, bridgeId }) {
    return bridgeBaseUrl && bridgeId
      ? `${bridgeBaseUrl} (${bridgeId})`
      : (bridgeBaseUrl ?? bridgeId);
  },
} satisfies EventProtocolView<"a2a">;

const mcpEventView = {
  label: "MCP",
  serverLabel: "MCP Server",
  unnamedLabel: "Unknown tool",
  callDetail(entry) {
    return commandSnippetFromArguments(entry.arguments);
  },
  callName(entry) {
    return entry.toolName;
  },
  icon(className) {
    return <ToolOutlined className={className} aria-hidden />;
  },
  serverName(entry) {
    return entry.mcpServerName;
  },
} satisfies EventProtocolView<"mcp">;

export const eventProtocolViews: Record<
  AgentActivityProtocol,
  EventProtocolView
> = {
  a2a: a2aEventView,
  mcp: mcpEventView,
};

/** The view for one entry's protocol. */
export const viewOf = (entry: AgentActivityEntry) =>
  eventProtocolViews[entry.protocol];
